import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {Panel2Loader} from '@wandb/weave/components/Panel2/PanelComp';
import {PanelContextProvider} from '@wandb/weave/components/Panel2/PanelContext';
import {PanelInteractContextProvider} from '@wandb/weave/components/Panel2/PanelInteractContext';
import {useRefineExpressionEffect} from '@wandb/weave/components/Panel2/panellib/libexp';
import {PanelRenderedConfigContextProvider} from '@wandb/weave/components/Panel2/PanelRenderedConfigContext';
import {useWeaveContext} from '@wandb/weave/context';
import {NotebookComputeGraphContextProvider} from '@wandb/weave/contextProviders';
import {
  constString,
  Node,
  opArtifactMembershipCollection,
  opArtifactMembershipCommitHash,
  opArtifactName,
  opArtifactProject,
  opDict,
  opEntityName,
  opGet,
  opProjectEntity,
  opProjectName,
  opStringAppend,
  varNode,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useCallback, useMemo, useState} from 'react';

import {RootQueryPanel} from './RootQueryPanel';

interface WeaveArtifactObjectProps {
  uri: string;
  varName: string;
}

export type PanelWeaveArtifactObjectProps = Panel2.PanelProps<
  'artifactMembership',
  any
>;

// convert an artifact name into a name that is safe to use in the expression editor
export function fixVarName(varName: string): string {
  return varName === '' ? 'obj' : varName.replace(/[^a-zA-Z0-9_]/g, '_');
}

export const PanelWeaveArtifactObject: React.FC<
  PanelWeaveArtifactObjectProps
> = props => {
  const {input: artifactMembershipNode} = props;

  const dataNode = useMemo(() => {
    const artifactNode = opArtifactMembershipCollection({
      artifactMembership: artifactMembershipNode,
    });
    const artifactNameNode = opArtifactName({artifact: artifactNode});
    const versionCommitHash = opArtifactMembershipCommitHash({
      artifactMembership: artifactMembershipNode,
    });
    const projectNode = opArtifactProject({artifact: artifactNode});
    const projectNameNode = opProjectName({project: projectNode});
    const entityNameNode = opEntityName({
      entity: opProjectEntity({project: projectNode}),
    });
    const entityProjectNameNode = opStringAppend({
      str: opStringAppend({str: entityNameNode, suffix: constString('/')}),
      suffix: projectNameNode,
    });

    const uriNode = opStringAppend({
      str: opStringAppend({
        str: opStringAppend({
          str: opStringAppend({
            str: opStringAppend({
              str: constString('wandb-artifact:///'),
              suffix: entityProjectNameNode,
            }),
            suffix: constString('/'),
          }),
          suffix: artifactNameNode,
        }),
        suffix: constString(':'),
      }),
      suffix: versionCommitHash,
    }) as Node<'string'>;

    return opDict({
      uri: uriNode,
      varName: artifactNameNode,
    } as any);
  }, [artifactMembershipNode]);

  const {result, loading} = useNodeValue(dataNode);

  return !loading ? (
    <NotebookComputeGraphContextProvider>
      <WeaveArtifactObject
        uri={result.uri}
        varName={fixVarName(result.varName)}
      />
    </NotebookComputeGraphContextProvider>
  ) : (
    <Panel2Loader />
  );
};

export const WeaveArtifactObject: React.FC<
  WeaveArtifactObjectProps
> = props => {
  const {uri, varName} = props;
  const weave = useWeaveContext();

  const [config, setConfig] = useState({
    panelConfig: {exp: varNode('invalid', varName)},
  });
  const updateConfig = useCallback(
    (newConfig: any) => {
      setConfig({...config, ...newConfig});
    },
    [config]
  );
  const objectNode = useMemo(
    () => opGet({uri: constString(uri + '/obj')}),
    [uri]
  );

  const frame = useMemo(() => [], []);

  const {loading: isRefining, result: refinedObjectNode} =
    useRefineExpressionEffect(objectNode as any, frame, weave);
  const vars = useMemo(
    () => ({
      [varName]: refinedObjectNode,
    }),
    [refinedObjectNode, varName]
  );

  if (isRefining) {
    return <WandbLoader name="weave-artifact-object-loader" />;
  }

  return (
    <div>
      <PanelContextProvider newVars={vars as any}>
        <PanelRenderedConfigContextProvider>
          <PanelInteractContextProvider>
            <RootQueryPanel config={config} updateConfig={updateConfig} />
          </PanelInteractContextProvider>
        </PanelRenderedConfigContextProvider>
      </PanelContextProvider>
    </div>
  );
};
